import React, {useState, useEffect, useRef} from "react";
import axios from "axios";
import {Button} from "primereact/button";
import {FloatLabel} from "primereact/floatlabel";
import {InputText} from "primereact/inputtext";
import {FileUpload} from "primereact/fileupload";
import {ProgressBar} from "primereact/progressbar";
import {Calendar} from "primereact/calendar";
import {AutoComplete} from "primereact/autocomplete";
import {ScrollPanel} from "primereact/scrollpanel";
import {Toast} from "primereact/toast";
import {Tooltip} from "primereact/tooltip";
import {contextTypes} from "./components/static/contect_types";
import {reportingVariables} from "./components/static/reporting_variables_static";
import {bootstrapColors, primeIcons} from "../static/colors_icons_ui";
import IconSelector from "./components/widgets/IconSelector";
import ColorSelector from "./components/widgets/ColorSelector";
import CreatingCard from "./components/widgets/CreatingCard";
import {base, reports_card} from "../../api";

export default function ReportingProcessesAddForm({
	setDialogVisible,
	dialogVisible,
	fetchReportings,
}) {
	const [selectedKeys, setSelectedKeys] = useState([]);
	const [formFields, setFormFields] = useState([]);
	const [formName, setFormName] = useState("");
	const [slug, setSlug] = useState("");
	const [selectedColor, setSelectedColor] = useState(bootstrapColors[0].color);
	const [selectedIcon, setSelectedIcon] = useState(primeIcons[0]);
	const [totalSize, setTotalSize] = useState(0);

	const toast = useRef(null);
	const fileUploadRef = useRef(null);

	// Available context types (filter out selected)
	const availableKeys = contextTypes.filter(
		contextType => !selectedKeys.some(key => key.type === contextType.type),
	);

	const availableReportingVars = reportingVariables.filter(
		reportingVariable => !formFields.some(field => field.type === reportingVariable.type),
	);

	// Reset form on dialog open/close
	useEffect(() => {
		if (!dialogVisible) {
			setFormName("");
			setSlug("");
			setSelectedColor(bootstrapColors[0].color);
			setSelectedIcon(primeIcons[0]);
			setSelectedKeys([]);
			setFormFields([]);
			setTotalSize(0);
			fileUploadRef.current?.clear();
		}
	}, [dialogVisible]);

	const handleChipClick = key => {
		setSelectedKeys(prev => [...prev, key]);
	};

	const handleRemoveInput = key => {
		setSelectedKeys(prev => prev.filter(item => item.type !== key.type));
	};

	const saveData = async () => {
		const formData = new FormData();

		formData.append("name", formName);
		formData.append("slug", slug);
		formData.append("color", selectedColor);
		formData.append("icon", selectedIcon);
		formData.append("form_fields", JSON.stringify(selectedKeys));
		formData.append("form_fields", JSON.stringify(formFields));
		formData.append("selected_contexts", JSON.stringify(selectedKeys.map(item => item.type)));

		const files = fileUploadRef.current?.getFiles() || [];
		files.forEach(file => {
			formData.append("uploaded_files", file);
		});

		try {
			await axios.post(base + reports_card, formData, {
				headers: {"Content-Type": "multipart/form-data"},
			});
			fetchReportings();
			setDialogVisible(false);
			toast.current.show({
				severity: "success",
				summary: "Success",
				detail: "Data saved successfully",
			});
		} catch (error) {
			console.error("Error saving data:", error);
			toast.current.show({
				severity: "error",
				summary: "Error",
				detail:
					typeof error?.response?.data === "object"
						? Object.values(error.response.data).flat().join(", ")
						: error?.response?.data || "Failed to save data",
			});
		}
	};

	// File upload helpers (same as before)
	const onTemplateSelect = e => {
		let _totalSize = totalSize;
		let files = e.files;
		Object.keys(files).forEach(key => {
			_totalSize += files[key].size || 0;
		});
		setTotalSize(_totalSize);
	};

	const onTemplateUpload = e => {
		let _totalSize = 0;
		e.files.forEach(file => {
			_totalSize += file.size || 0;
		});
		setTotalSize(_totalSize);
		toast.current.show({severity: "info", summary: "Success", detail: "File Uploaded"});
	};

	const onTemplateRemove = (file, callback) => {
		setTotalSize(totalSize - file.size);
		callback();
	};

	const onTemplateClear = () => {
		setTotalSize(0);
	};

	const headerTemplate = options => {
		const {className, chooseButton} = options;
		const formattedSize = fileUploadRef.current
			? fileUploadRef.current.formatSize(totalSize)
			: "0 B";
		return (
			<div className={className} style={{display: "flex", alignItems: "center"}}>
				{chooseButton}
				<div className="ml-auto d-flex align-items-center gap-3">
					<span>{formattedSize} / 1 MB</span>
					<ProgressBar value={totalSize / 10000} showValue={false} style={{width: "10rem"}} />
				</div>
			</div>
		);
	};

	const itemTemplate = (file, props) => (
		<div className="w-100 d-flex justify-content-between align-items-center py-0">
			<span className="text-start text-primary">
				{file.name}
				<small className="text-info ms-1">({props.formatSize})</small>
			</span>
			<Button
				type="button"
				icon="pi pi-times"
				text
				rounded
				severity="danger"
				onClick={() => onTemplateRemove(file, props.onRemove)}
			/>
		</div>
	);

	const emptyTemplate = () => (
		<div className="flex align-items-center flex-column">
			<i
				className="pi pi-image mt-2 p-3"
				style={{
					fontSize: "1.5em",
					borderRadius: "50%",
					backgroundColor: "var(--surface-b)",
					color: "var(--surface-d)",
				}}></i>
			<span style={{fontSize: "1.2em", color: "var(--text-color-secondary)"}} className="mt-2">
				Drag and Drop Image Here
			</span>
		</div>
	);

	const chooseOptions = {
		icon: "pi pi-arrow-up",
		iconOnly: true,
		className: "custom-choose-btn p-button-rounded p-button-outlined",
	};

	// Scroll dialog content to top when opened
	useEffect(() => {
		const dialogContent = document.querySelector(".p-dialog .p-dialog-content");
		if (dialogContent) dialogContent.scrollTop = 0;
	}, [dialogVisible]);

	return (
		<div className="col-12">
			<Toast ref={toast} />
			<div className="row">
				<div className="col-12 fs-5 fw-bold opacity-50 border-bottom pb-2 mb-2 p-0">
					Card example
				</div>
				<div className="col-xl-3 col-lg-4 col-md-4 col-12 stretch-card p-3">
					<CreatingCard
						selectedIcon={selectedIcon}
						formName={formName}
						selectedColor={selectedColor}
					/>
				</div>
				<div className="col-xl-9 col-lg-8 col-md-8 col-12 stretch-card p-3">
					<div className="row">
						<FloatLabel className="col-6">
							<InputText
								className="w-100"
								id="formName"
								value={formName}
								onChange={e => {
									setFormName(e.target.value);
									setSlug(
										e.target.value
											.toLowerCase()
											.replace(/\s+/g, "-")
											.replace(/[^a-z0-9_-]/g, ""),
									);
								}}
							/>
							<label htmlFor="formName" className="ps-3 pt-0">
								Card Title
							</label>
						</FloatLabel>
						<FloatLabel className="col-6">
							<InputText
								className="w-100"
								id="slug"
								value={slug}
								onChange={e =>
									setSlug(
										e.target.value
											.toLowerCase()
											.replace(/\s+/g, "-")
											.replace(/[^a-z0-9_-]/g, ""),
									)
								}
							/>
							<label htmlFor="slug" className="ps-3 pt-0">
								Slug/url
							</label>
						</FloatLabel>
						<IconSelector
							selectedIcon={selectedIcon}
							setSelectedIcon={setSelectedIcon}
							selectedColor={selectedColor}
						/>
						<ColorSelector selectedColor={selectedColor} setSelectedColor={setSelectedColor} />
					</div>
				</div>

				<div className="row">
					<div className="col-12 fs-5 fw-bold opacity-50 border-bottom pb-2 mb-2 p-0">
						Form example
					</div>
					<div className="col-12">
						<div className="d-flex flex-wrap gap-2 mb-3">
							{availableKeys.map(item => (
								<Button
									key={item.name}
									label={item.name}
									icon="pi pi-plus"
									className="m-1"
									rounded
									outlined
									severity="secondary"
									onClick={() => handleChipClick(item)}
								/>
							))}
						</div>
						<hr />
						{selectedKeys.map(item => (
							<Button
								key={item.key || item.name}
								label={item.key || item.name}
								icon="pi pi-times"
								className="m-1"
								rounded
								outlined
								severity="secondary"
								onClick={() => handleRemoveInput(item)}
							/>
						))}
						{selectedKeys.length === 0 && (
							<div className="d-flex justify-content-center align-items-center p-5 opacity-50">
								Selected variables will be shown here
							</div>
						)}
					</div>
				</div>

				<div className="col-12 fs-5 fw-bold opacity-50 border-bottom pb-2 mb-2 p-0">Variables</div>
				<div className="col-12">
					<div className="d-flex flex-wrap gap-2 mb-3">
						{availableReportingVars.map(item => (
							<Button
								key={item.variable}
								label={item.description}
								icon="pi pi-plus"
								className="m-1"
								rounded
								outlined
								severity="secondary"
								onClick={() => setFormFields(prev => [...prev, item])}
							/>
						))}
					</div>
					{availableReportingVars.length > 0 && <hr />}
					<div className="col-12 mb-3">
						<div className="row">
							{formFields.map(item => (
								<div className="col-sm-6 col-12 mb-3" key={item.variable}>
									<label className="d-block fw-semibold">{item.description}</label>
									<small className="text-danger d-block mb-1 opacity-75">
										{item.type} - {item.field}
									</small>
									{item.field === "calendar" ? (
										<div className="d-flex align-items-center justify-content-center">
											<Calendar className="w-100" showIcon disabled />
											<Button
												rounded
												icon="pi pi-trash"
												className="border-0 ms-2"
												outlined
												severity="danger"
												onClick={() =>
													setFormFields(prev => prev.filter(v => v.type !== item.type))
												}
											/>
										</div>
									) : item.field === "autocomplate" ? (
										<div className="d-flex align-items-center justify-content-center">
											<AutoComplete className="w-100" dropdown disabled />
											<Button
												rounded
												icon="pi pi-trash"
												className="border-0 ms-2"
												outlined
												severity="danger"
												onClick={() =>
													setFormFields(prev => prev.filter(v => v.type !== item.type))
												}
											/>
										</div>
									) : null}
								</div>
							))}
						</div>
					</div>
				</div>

				<div>
					<div className="col-12 fs-5 fw-bold opacity-50 border-bottom pb-2 mb-2 p-0">
						Upload all templates
					</div>
					<Tooltip target=".custom-choose-btn" content="Choose" position="bottom" />
					<Tooltip target=".custom-upload-btn" content="Upload" position="bottom" />
					<Tooltip target=".custom-cancel-btn" content="Clear" position="bottom" />
					<FileUpload
						ref={fileUploadRef}
						name="demo[]"
						url="/api/upload"
						multiple
						accept=".doc, .docx, .xls, .xlsx"
						maxFileSize={1000000}
						onUpload={onTemplateUpload}
						onSelect={onTemplateSelect}
						onError={onTemplateClear}
						onClear={onTemplateClear}
						headerTemplate={headerTemplate}
						itemTemplate={itemTemplate}
						emptyTemplate={emptyTemplate}
						chooseOptions={chooseOptions}
					/>
				</div>
			</div>

			<div className="col-12 mt-5 text-center border-top pt-3">
				<Button
					label="No"
					icon="pi pi-times"
					onClick={() => setDialogVisible(false)}
					className="mx-3"
					rounded
					text
					severity="secondary"
				/>
				<Button
					label="Yes"
					icon="pi pi-check"
					className="mx-3"
					rounded
					onClick={saveData}
					autoFocus
				/>
			</div>
		</div>
	);
}
